import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";
import {
  useHealthHistoryStore,
  INFO_QUESTIONS,
  CONDITION_LABELS,
  MEDICINE_QUESTIONS,
  MEDICINE2_QUESTIONS,
} from "@/store/healthHistory";
import taskRequest from "@/api/task-request";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";

/** Resolve case history id: use existing or find/create one so health history can be saved. */
async function resolveCaseHistoryId(
  facilityKey: string,
  patientKey: string,
  setCurrentCaseHistoryId: (id: number) => void
): Promise<number | null> {
  const existing = useTaskStore.getState().caseHistory?.id;
  if (existing != null && existing > 0) return existing;
  try {
    const result = await taskRequest.request.findIncompleteCaseHistory(
      facilityKey,
      patientKey
    );
    let id: number | undefined =
      typeof result?.data === "number"
        ? result.data
        : (result?.data as { id?: number } | undefined)?.id;
    if (id == null || id === 0) {
      const insert = await taskRequest.request.insertCaseHistory(
        facilityKey,
        patientKey,
        { aa: "false" }
      );
      id =
        typeof insert?.data === "number"
          ? insert.data
          : (insert?.data as { id?: number } | undefined)?.id;
    }
    if (id != null && id > 0) {
      setCurrentCaseHistoryId(id);
      return id;
    }
  } catch (e) {
    console.error("Resolve case history failed:", e);
  }
  return null;
}

export function HealthHistory3Page() {
  const navigate = useNavigate();
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const caseHistoryId = useTaskStore((s) => s.caseHistory?.id);
  const setCurrentCaseHistoryId = useTaskStore((s) => s.setCurrentCaseHistoryId);
  const facilityKey = getFacilityKeyForPortal();
  const patientKey = getPatientKey();
  const isFemale = (currentPatient?.sex ?? "").toUpperCase() === "F";

  const infoAnswers = useHealthHistoryStore((s) => s.infoAnswers);
  const setInfoAnswers = useHealthHistoryStore((s) => s.setInfoAnswers);
  const conditions = useHealthHistoryStore((s) => s.conditions);
  const medicineAnswers = useHealthHistoryStore((s) => s.medicineAnswers);
  const medicine2Answers = useHealthHistoryStore((s) => s.medicine2Answers);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const medicineQuestionsWithOptional = [
    ...MEDICINE_QUESTIONS,
    ...(isFemale ? [{ code: "BirthControl", question: "Birth control" }] : []),
  ];
  const medicine2WithOptional = [
    ...MEDICINE2_QUESTIONS,
    ...(isFemale
      ? [
          { code: "Pregnant", question: "Are you pregnant?", isDate: false as const },
          { code: "LastMenstrualPeriod", question: "Date of last menstrual period?", isDate: true as const },
        ]
      : []),
  ];

  const goBack = () => navigate("/complaint/health-history-2");
  const goToFamilyHistory = () => navigate("/complaint/family-history");

  const handleSaveAndContinue = async () => {
    if (!facilityKey || !patientKey) {
      goToFamilyHistory();
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      const resolvedId =
        caseHistoryId != null && caseHistoryId > 0
          ? caseHistoryId
          : await resolveCaseHistoryId(
              facilityKey,
              patientKey,
              setCurrentCaseHistoryId
            );
      if (resolvedId == null || resolvedId === 0) {
        setSaveError("Could not find or create a case record. Please start from the dashboard task.");
        return;
      }
      const questions: { description: string; answer: string }[] = [];
      INFO_QUESTIONS.forEach((i) => {
        if (infoAnswers[i.code]) questions.push({ description: i.code, answer: infoAnswers[i.code] });
      });
      medicineQuestionsWithOptional.forEach((m) => {
        const a = medicineAnswers[m.code];
        const answer = a?.answer ?? "";
        if (answer !== "Yes" && answer !== "No") return;
        const ans = answer === "Yes" ? (a?.year ? `Yes,${a.year}` : "Yes") : "No";
        questions.push({ description: m.code, answer: ans });
      });
      medicine2WithOptional.forEach((m) => {
        const v = medicine2Answers[m.code];
        if (v !== undefined && v !== "") questions.push({ description: m.code, answer: v });
      });
      await taskRequest.request.updateCaseHistoryQuestions(
        facilityKey,
        { caseHistoryId: resolvedId, questions }
      );
      await taskRequest.request.clearCaseHistoryConditions(
        facilityKey,
        resolvedId
      );
      await taskRequest.request.updateCaseHistoryConditions(
        facilityKey,
        resolvedId,
        CONDITION_LABELS.map((desc) => {
          const c = conditions[desc] ?? { present: false, past: false };
          return {
            description: desc,
            present: c.present,
            past: c.past,
            notPresent: !c.present && !c.past,
          };
        })
      );
      goToFamilyHistory();
    } catch (err) {
      console.error("Health history save failed:", err);
      setSaveError(
        err instanceof Error ? err.message : "Failed to save. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentPatient) {
    return (
      <div className="wizard-page w-full">
        <div className="wizard-page__content px-4 pt-4 pb-0">
          <p className="text-sm text-slate-600">Please sign in or select a patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content px-4 pt-4 pb-0">
        <div className="complaint-history-card">
          <div className="complaint-history-heading">
            <div className="complaint-history-heading-bar" />
            <h2 className="complaint-history-heading-text">Please answer these questions about yourself</h2>
          </div>
          <div className="complaint-history-divider" />

          <div className="complaint-history-section-block">
            {INFO_QUESTIONS.map((iq) => (
              <ChoiceGroup
                key={iq.code}
                id={`info-${iq.code}`}
                clearable
                layout={iq.code === "NumberOfChildren" ? "numbers" : "pills"}
                legend={iq.question}
                options={iq.options.map((opt) => ({ value: opt, label: opt }))}
                value={infoAnswers[iq.code] || null}
                onChange={(v) => setInfoAnswers((prev) => ({ ...prev, [iq.code]: v ?? "" }))}
              />
            ))}
          </div>
        </div>
      </div>
      {saveError && (
        <p className="mx-4 mt-2 text-sm text-red-600" role="alert">
          {saveError}
        </p>
      )}
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>
          {t.backButton}
        </Button>
        <Button type="button" onClick={handleSaveAndContinue} disabled={isSaving}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
